import { CompanionCardHeader } from '@/components/companion/cards/CompanionCardHeader';
import { CompanionCardShell } from '@/components/companion/cards/CompanionCardShell';
import CreatorAvatar from '@/components/creators/CreatorAvatar';
import { MatchFlames } from '@/components/recommendations/MatchFlames';
import { Box, Button, HStack, Text } from '@chakra-ui/react';
import type {
  CompanionActionDto,
  CompanionBrandmatchBatchEvidence,
  CompanionBrandmatchPreviewProfile,
  MatchTier,
} from '@fluenceur/types';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The brand-match batch card, as the companion panel shows it.
 *
 * A presenter and nothing else: it renders the evidence the server froze when
 * the card was published and never asks a question of its own. In particular it
 * never states how many profiles were analysed — that number is not in the
 * evidence, and a card that invents one is a card the brand cannot check.
 */

/** The scale the flames read, back from the count the server stamped. */
const TIER_BY_FLAMES: Record<number, MatchTier> = {
  3: 'excellent',
  2: 'good',
  1: 'moderate',
  0: 'unfit',
};

interface BrandmatchBatchCardProps {
  action: CompanionActionDto & { evidence: CompanionBrandmatchBatchEvidence };
  /** Opens the batch in Discovery. Wired by the panel, never by this card. */
  onOpenCta: () => void;
  /** Starts the dismissal choice. Absent while the panel does not offer it. */
  onDismiss?: () => void;
  isCtaPending?: boolean;
  /** The workflow has no adapter in this build; the button is shown inert. */
  isCtaDisabled?: boolean;
  /** Fills the panel edge to edge: no frame of its own, the panel is the frame. */
  flat?: boolean;
  /** The host's own controls, rendered at the end of the action row. */
  footerEnd?: ReactNode;
}

function labelOf(profile: CompanionBrandmatchPreviewProfile, fallback: string): string {
  return profile.displayName ?? profile.handle ?? fallback;
}

/**
 * The platforms whose handle is also the id the avatar endpoint answers to.
 * The batch freezes a handle, never a creator id, so this is the only way a
 * stored image that has since expired can still be refreshed.
 */
const HANDLE_IS_AVATAR_ID = new Set(['instagram', 'ig']);

const PreviewRow = ({
  profile,
  fallbackLabel,
}: {
  profile: CompanionBrandmatchPreviewProfile;
  fallbackLabel: string;
}) => {
  const label = labelOf(profile, fallbackLabel);
  const avatarCreatorId = HANDLE_IS_AVATAR_ID.has(profile.platform)
    ? (profile.handle ?? undefined)
    : undefined;

  return (
    <HStack
      gap="10px"
      align="center"
      px="12px"
      py="10px"
      bg="panelBg"
      borderBottomWidth="1px"
      borderColor="cardBorder"
      _last={{ borderBottomWidth: 0 }}
      data-testid="brandmatch-preview-row"
    >
      <CreatorAvatar
        creatorId={avatarCreatorId}
        creatorName={label}
        fallbackSrc={profile.avatarUrl ?? undefined}
        lazyFetch={Boolean(avatarCreatorId && profile.avatarUrl)}
        size="xs"
      />
      <Box flex={1} minW={0}>
        <Text
          fontFamily="heading"
          fontSize="13px"
          fontWeight="600"
          color="ink"
          lineClamp={1}
          lineHeight="1.3"
        >
          {label}
          {profile.handle && profile.displayName ? (
            <Text as="span" fontWeight="400" color="inkSubtle" ml="6px">
              {profile.handle}
            </Text>
          ) : null}
        </Text>
      </Box>
      <MatchFlames tier={TIER_BY_FLAMES[profile.matchFlames] ?? 'unfit'} size="xs" flexShrink={0} />
    </HStack>
  );
};

export const BrandmatchBatchCard = ({
  action,
  onOpenCta,
  onDismiss,
  isCtaPending = false,
  isCtaDisabled = false,
  flat = false,
  footerEnd,
}: BrandmatchBatchCardProps) => {
  const { t } = useTranslation();
  const { evidence } = action;
  const remaining = Math.max(0, evidence.creatorCount - evidence.previewProfiles.length);

  return (
    <CompanionCardShell
      testId="companion-brandmatch-batch-card"
      dataAttrs={{ 'data-batch-id': evidence.batchId }}
      flat={flat}
      ctaLabel={action.ctaLabel}
      onOpenCta={onOpenCta}
      onDismiss={onDismiss}
      isCtaPending={isCtaPending}
      isCtaDisabled={isCtaDisabled}
      footerEnd={footerEnd}
    >
      <CompanionCardHeader title={action.title} reason={action.reason} />

      {evidence.previewProfiles.length > 0 ? (
        <Box borderWidth="1px" borderColor="cardBorder" borderRadius="10px" overflow="hidden">
          {evidence.previewProfiles.map((profile) => (
            <PreviewRow
              key={profile.socialProfileId}
              profile={profile}
              fallbackLabel={t('companion.cards.brandmatchBatch.unknownCreator')}
            />
          ))}
          {remaining > 0 ? (
            <Button
              variant="plain"
              w="full"
              h="auto"
              justifyContent="flex-start"
              px="12px"
              py="8px"
              borderRadius="0"
              bg="softCream"
              fontFamily="heading"
              fontSize="12px"
              fontWeight="600"
              color="inkMuted"
              _hover={{ color: 'ink', bg: 'softCream' }}
              onClick={onOpenCta}
              data-testid="brandmatch-preview-more"
            >
              {t('companion.cards.brandmatchBatch.more', { count: remaining })}
            </Button>
          ) : null}
        </Box>
      ) : null}
    </CompanionCardShell>
  );
};
